import { createSlice } from "@reduxjs/toolkit"
import {
  addBrand,
  deleteBrand,
  getAllBrands,
  getBrandDetails,
  getBrandList,
  getBrandNamesList,
  getAssignedBrands,
  setDefaultBrand,
  toggleBrand,
  updateBrand,
  updateBrandStatus,
} from "store/actions/brandsAction"
import {
  saAddBrand,
  saDeleteBrand,
  saGetAllBrands,
  saGetBrandDetails,
  saGetBrandList,
  saGetBrandNamesList,
  saGetBrandPaymentMethods,
  saToggleBrand,
  saUpdateBrand,
  saGetTenantFulfillmentIntegrations,
  saGetDeliveryProfileTemplates,
} from "store/actions/superAdminAction"

const initialState = {
  loading: true,
  isLoading: false,
  brandList: [],
  brands: [],
  brandDetails: null,
  brandNames: [],
  paymentMethodOptions: [],
  totalCount: 0,
  totalPages: 1,
  currentPage: 1,
  itemsPerPage: 10,
  assignedBrands: [],
  assignedBrandsLoading: false,
  assignedBrandsLoaded: false,
  defaultBrandId: null,
  fulfillmentIntegrations: [],
  fulfillmentIntegrationsLoading: false,
  deliveryProfileTemplates: [],
  deliveryProfileTemplatesLoading: false,
}

const brandsSlice = createSlice({
  name: "brands",
  initialState,
  reducers: {
    clearBrandDetails(state) {
      state.brandDetails = null
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getBrandList.pending, state => {
        state.loading = true
      })
      .addCase(getBrandList.fulfilled, (state, action) => {
        state.loading = false
        const { data } = action.payload || {}
        state.brandList = Array.isArray(data) ? data : data?.data || []
      })
      .addCase(getBrandList.rejected, state => {
        state.loading = false
      })

    builder
      .addCase(saGetBrandList.pending, state => {
        state.loading = true
      })
      .addCase(saGetBrandList.fulfilled, (state, action) => {
        state.loading = false
        const { data } = action.payload || {}
        state.brandList = Array.isArray(data) ? data : data?.data || []
      })
      .addCase(saGetBrandList.rejected, state => {
        state.loading = false
      })

    builder
      .addCase(getBrandNamesList.fulfilled, (state, action) => {
        const { data } = action.payload || {}
        state.brandNames = Array.isArray(data) ? data : []
      })
      .addCase(saGetBrandNamesList.fulfilled, (state, action) => {
        const { data } = action.payload || {}
        state.brandNames = Array.isArray(data) ? data : []
      })
      .addCase(saGetBrandPaymentMethods.fulfilled, (state, action) => {
        const { data } = action.payload || {}
        state.paymentMethodOptions = Array.isArray(data) ? data : []
      })

    builder
      .addCase(getAllBrands.pending, state => {
        state.loading = true
      })
      .addCase(getAllBrands.fulfilled, (state, action) => {
        state.loading = false
        const payload = action.payload || {}
        state.brands = payload.data || []
        state.totalCount = payload.totalCount ?? state.brands.length
        state.totalPages = payload.totalPages ?? 1
        state.currentPage = payload.currentPage ?? 1
        state.itemsPerPage = payload.itemsPerPage ?? 10
      })
      .addCase(getAllBrands.rejected, state => {
        state.loading = false
      })

    builder
      .addCase(saGetAllBrands.pending, state => {
        state.loading = true
      })
      .addCase(saGetAllBrands.fulfilled, (state, action) => {
        state.loading = false
        const payload = action.payload || {}
        state.brands = payload.data || []
        state.totalCount = payload.totalCount ?? state.brands.length
        state.totalPages = payload.totalPages ?? 1
        state.currentPage = payload.currentPage ?? 1
        state.itemsPerPage = payload.itemsPerPage ?? 10
      })
      .addCase(saGetAllBrands.rejected, state => {
        state.loading = false
      })

    builder
      .addCase(getBrandDetails.pending, state => {
        state.loading = true
      })
      .addCase(getBrandDetails.fulfilled, (state, action) => {
        state.loading = false
        state.brandDetails = action.payload?.data || null
      })
      .addCase(getBrandDetails.rejected, state => {
        state.loading = false
      })

    builder
      .addCase(saGetBrandDetails.pending, state => {
        state.loading = true
      })
      .addCase(saGetBrandDetails.fulfilled, (state, action) => {
        state.loading = false
        state.brandDetails = action.payload?.data || null
      })
      .addCase(saGetBrandDetails.rejected, state => {
        state.loading = false
      })

    builder
      .addCase(addBrand.pending, state => {
        state.isLoading = true
      })
      .addCase(addBrand.fulfilled, state => {
        state.isLoading = false
      })
      .addCase(addBrand.rejected, state => {
        state.isLoading = false
      })
      .addCase(saAddBrand.pending, state => {
        state.isLoading = true
      })
      .addCase(saAddBrand.fulfilled, state => {
        state.isLoading = false
      })
      .addCase(saAddBrand.rejected, state => {
        state.isLoading = false
      })

    builder
      .addCase(updateBrand.pending, state => {
        state.isLoading = true
      })
      .addCase(updateBrand.fulfilled, state => {
        state.isLoading = false
      })
      .addCase(updateBrand.rejected, state => {
        state.isLoading = false
      })
      .addCase(saUpdateBrand.pending, state => {
        state.isLoading = true
      })
      .addCase(saUpdateBrand.fulfilled, state => {
        state.isLoading = false
      })
      .addCase(saUpdateBrand.rejected, state => {
        state.isLoading = false
      })

    builder
      .addCase(deleteBrand.pending, state => {
        state.isLoading = true
      })
      .addCase(deleteBrand.fulfilled, state => {
        state.isLoading = false
      })
      .addCase(deleteBrand.rejected, state => {
        state.isLoading = false
      })
      .addCase(saDeleteBrand.pending, state => {
        state.isLoading = true
      })
      .addCase(saDeleteBrand.fulfilled, state => {
        state.isLoading = false
      })
      .addCase(saDeleteBrand.rejected, state => {
        state.isLoading = false
      })

    builder
      .addCase(updateBrandStatus.pending, state => {
        state.isLoading = true
      })
      .addCase(updateBrandStatus.fulfilled, state => {
        state.isLoading = false
      })
      .addCase(updateBrandStatus.rejected, state => {
        state.isLoading = false
      })

    builder
      .addCase(toggleBrand.pending, state => {
        state.isLoading = true
      })
      .addCase(toggleBrand.fulfilled, state => {
        state.isLoading = false
      })
      .addCase(toggleBrand.rejected, state => {
        state.isLoading = false
      })
      .addCase(saToggleBrand.pending, state => {
        state.isLoading = true
      })
      .addCase(saToggleBrand.fulfilled, state => {
        state.isLoading = false
      })
      .addCase(saToggleBrand.rejected, state => {
        state.isLoading = false
      })

    builder
      .addCase(getAssignedBrands.pending, state => {
        state.assignedBrandsLoading = true
      })
      .addCase(getAssignedBrands.fulfilled, (state, action) => {
        state.assignedBrandsLoading = false
        state.assignedBrandsLoaded = true
        state.assignedBrands = action.payload?.data?.brands || []
        state.defaultBrandId = action.payload?.data?.defaultBrandId || null
      })
      .addCase(getAssignedBrands.rejected, state => {
        state.assignedBrandsLoading = false
        state.assignedBrandsLoaded = true
        state.assignedBrands = []
        state.defaultBrandId = null
      })

    builder
      .addCase(setDefaultBrand.fulfilled, (state, action) => {
        const nextDefaultBrandId = action.payload?.data?.defaultBrandId || null
        state.defaultBrandId = nextDefaultBrandId
        state.assignedBrands = (state.assignedBrands || []).map(brand => ({
          ...brand,
          isDefault: brand.brandId === nextDefaultBrandId,
        }))
      })
    builder
      .addCase(saGetTenantFulfillmentIntegrations.pending, state => {
        state.fulfillmentIntegrationsLoading = true
      })
      .addCase(saGetTenantFulfillmentIntegrations.fulfilled, (state, action) => {
        state.fulfillmentIntegrationsLoading = false
        state.fulfillmentIntegrations = Array.isArray(action.payload?.data)
          ? action.payload.data
          : []
      })
      .addCase(saGetTenantFulfillmentIntegrations.rejected, state => {
        state.fulfillmentIntegrationsLoading = false
        state.fulfillmentIntegrations = []
      })
    builder
      .addCase(saGetDeliveryProfileTemplates.pending, state => {
        state.deliveryProfileTemplatesLoading = true
      })
      .addCase(saGetDeliveryProfileTemplates.fulfilled, (state, action) => {
        state.deliveryProfileTemplatesLoading = false
        state.deliveryProfileTemplates = Array.isArray(action.payload?.data)
          ? action.payload.data
          : []
      })
      .addCase(saGetDeliveryProfileTemplates.rejected, state => {
        state.deliveryProfileTemplatesLoading = false
        state.deliveryProfileTemplates = []
      })
  },
})

export const { clearBrandDetails } = brandsSlice.actions
export default brandsSlice.reducer
